import React, { useContext, useEffect } from 'react';
import {
    Box,
    Text,
    Flex,
    IconButton,
    CircularProgress,
    CircularProgressLabel,
    Image
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { useQuery, useApolloClient } from '@apollo/client';
import { EDSHOP_VIDEO } from '../../graphql/queries';
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';
import { extractTextFromFile } from '../../utils/helper';

export const UploadingVideoCard = ({ video, onDelete }) => {
    const apolloClient = useApolloClient();
    const { publisher } = useContext(PublisherAndUserContext);

    const { data, stopPolling } = useQuery(EDSHOP_VIDEO, {
        variables: {
            where: {
                id: publisher?.id,
                videoId: video.id
            }
        },
        skip: video.uploadProgress < 100, // Skip query until upload is 100%
        pollInterval: 60000, // Poll every 60 seconds
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (data?.edshopifyVideo?.status === "available") {
            stopPolling(); // Stop checking once video is available
            extractTextFromFile("video/extract-text", JSON.stringify({
                videoId: data?.edshopifyVideo?.id,
                uuid: data?.edshopifyVideo?.id,
                publisherId: publisher?.id
            }))
            apolloClient.cache.modify({
                fields: {
                    edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                        return {
                            ...existingedshopifyVideosRefs,
                            videos: existingedshopifyVideosRefs.videos.map((v) =>
                                readField("id", v) === video?.id ? { ...v, ...data?.edshopifyVideo } : v
                            ),
                        };
                    },
                },
            });
        }
    }, [apolloClient.cache, data, stopPolling, video?.id]);

    return (
        <Box
            key={video.id}
            bg="white"
            boxShadow="md"
            borderRadius="lg"
            overflow="hidden"
            position="relative"
            _hover={{ transform: "scale(1.02)", transition: "0.2s" }}
        >
            {/* Video Thumbnail */}
            <Box
                bg="gray.100"
                overflow="hidden"
                position="relative"
                paddingBottom="56.25%" // Maintain 16:9 aspect ratio
                display="flex"
                justifyContent="center"
                alignItems="center"
                background="#777"
            >
                <Image
                    src="https://i.vimeocdn.com/video/default-2308240" // Fallback for missing thumbnail
                    alt={video.name}
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    width="100%"
                    height="100%"
                    objectFit="cover"
                />

                {/* Centered Upload Progress */}
                {video.uploadProgress < 100 ?
                    <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                    >
                        <CircularProgress value={video.uploadProgress} color='green.400'>
                            <CircularProgressLabel color="#fff">{video.uploadProgress}%</CircularProgressLabel>
                        </CircularProgress>
                    </Box> :
                    <Text
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        bg="rgba(0,0,0,0.6)"
                        color="#fff"
                        fontWeight="bold"
                        p={2}
                        borderRadius="md"
                    >
                        Processing...
                    </Text>
                }
            </Box>

            {/* Video Details */}
            <Box p={2}>
                <Text fontWeight="bold" isTruncated>
                    {video.name}
                </Text>
                <Flex justify="space-between" mt={2} align="center">
                    <Text fontSize="sm" color="gray.500">
                        {video.uploadProgress < 100 ? `Uploading... ${video.uploadProgress}%` : "Processing..."}
                    </Text>
                    <IconButton
                        aria-label="Cancel Upload"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => onDelete(video.id)}
                    />
                </Flex>
            </Box>
        </Box>
    );
};
